import React, { useEffect, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Button, Typography } from "@mui/material"
import TimerIcon from "@mui/icons-material/HourglassTop"

const useStyles = makeStyles()((theme) => ({
  // NOT a full-screen scrim, unlike KickedModal/ReconnectModal. A scrim here
  // would block the one thing that clears this warning — using the chat — and
  // would swallow the mousemove/keydown that dismiss it automatically. So the
  // wrapper is pointer-events:none and only the card itself is interactive.
  wrap: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: theme.spacing(3),
    zIndex: theme.zIndex.modal + 5,
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none",
  },
  card: {
    pointerEvents: "auto",
    maxWidth: "min(560px, 92vw)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
  },
  icon: { fontSize: 28, color: theme.palette.warning.main, flexShrink: 0 },
  text: { display: "flex", flexDirection: "column", minWidth: 0 },
  title: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  detail: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
  },
  countdown: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
    color: theme.palette.warning.main,
  },
  button: {
    flexShrink: 0,
    backgroundColor: theme.palette.primary.main,
    color: "#000",
    fontWeight: 600,
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: theme.palette.primary.dark },
  },
}))

const fmt = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

/**
 * "Still there?" banner for the AFK idle policy.
 *
 * Shown when the server says this user has gone idle and will be disconnected
 * unless they show signs of life. Any real input also clears it (the server
 * sends idle:warning-cleared on the next activity ping), so the button is a
 * convenience, not the only exit.
 *
 * Props:
 *   deadline - epoch ms when the disconnect happens, or null to hide. Computed
 *              CLIENT-side from the duration the server sent, so a skewed local
 *              clock can never render a wrong or negative countdown.
 *   onStillHere - emit an immediate activity ping
 *   confirming - true briefly after clicking, to show the click registered
 */
const IdleWarningBanner = ({ deadline, onStillHere, confirming = false }) => {
  const { classes } = useStyles()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!deadline) return undefined
    setNow(Date.now())
    // Re-read the clock each tick rather than decrementing a counter. A
    // backgrounded tab throttles this interval to ~1/min, so a decrementing
    // counter would drift arbitrarily far behind; this one just renders a stale
    // value and snaps to the truth the moment the tab is foregrounded.
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [deadline])

  if (!deadline) return null
  const remaining = deadline - now

  return (
    <div className={classes.wrap}>
      <div className={classes.card}>
        <TimerIcon className={classes.icon} />
        <div className={classes.text}>
          <Typography className={classes.title}>Still there?</Typography>
          <Typography className={classes.detail}>
            You&apos;ll be disconnected for inactivity in{" "}
            <span className={classes.countdown}>{fmt(remaining)}</span>.
          </Typography>
        </div>
        <Button
          className={classes.button}
          variant="contained"
          size="small"
          onClick={onStillHere}
          disabled={confirming}
        >
          {confirming ? "Thanks…" : "I'm still here"}
        </Button>
      </div>
    </div>
  )
}

export default IdleWarningBanner
